import { OurNode } from "./our-node";
import { TransferredComparer } from "./transferred-comparer";
interface NodesetParams {
  flatMap: Map<string, OurNode>;
  filteredFlatMap?: Map<string, OurNode>;
  rootNode: OurNode;
}

export class OurNodeset {
  flatMap: Map<string, OurNode>;
  filteredFlatMap: Map<string, OurNode>;
  rootNode: OurNode;
  selectedNodeIds: Set<string>;
  seletedHiddenNodesSet: Set<string>;
  observers: Set<() => void>;
  private showFilterPanel: boolean;

  constructor({ flatMap, filteredFlatMap, rootNode }: NodesetParams) {
    this.flatMap = flatMap ?? new Map<string, OurNode>();
    this.filteredFlatMap = filteredFlatMap || new Map<string, OurNode>();
    this.setRootNode(rootNode);
    this.selectedNodeIds = new Set();
    this.seletedHiddenNodesSet = new Set();
    this.observers = new Set();
    this.showFilterPanel = false;
  }

  clear() {
    this.setRootNode(undefined);
    this.setFlatMap(new Map<string, OurNode>());
    this.setFilteredFlatMap(new Map<string, OurNode>());
    this.selectedNodeIds = new Set();
    this.seletedHiddenNodesSet = new Set();
    this.observers = new Set();
    this.showFilterPanel = false;
  }

  getShowFilterPanel() {
    return this.showFilterPanel;
  }

  setShowFilterPanel(showFilterPanel: boolean) {
    this.showFilterPanel = showFilterPanel;
    this.notifyObservers(); // Notify on set
  }

  getFlatMap() {
    return this.flatMap;
  }

  getFilteredFlatMap() {
    return this.filteredFlatMap;
  }

  getRootNode() {
    return this.rootNode;
  }

  getNodeById(id: string): OurNode | null {
    return this.flatMap.get(id) || null;
  }

  getSelectedNodeCount(): number {
    return this.selectedNodeIds.size;
  }

  setSelectedNodeIds(selectedNodeIds: Set<string>): void {
    this.selectedNodeIds = selectedNodeIds;
    this.notifyObservers();
  }

  getSelectedVariablesCount(): number {
    let count = 0;
    const selectedNodesSet = this.selectedNodeIds;
    const machineFlatMap = this.getFlatMap();

    if (machineFlatMap.size > 0) {
      selectedNodesSet.forEach((nodeId) => {
        const node = machineFlatMap.get(nodeId);
        if (node.node_type === "VARIABLE") {
          count++;
        }
      });
    }

    return count;
  }

  setFlatMap(flatMap: Map<string, OurNode>) {
    this.flatMap = flatMap ?? new Map<string, OurNode>();
    this.notifyObservers(); // Notify on set
  }

  setFilteredFlatMap(filteredFlatMap: Map<string, OurNode>) {
    this.filteredFlatMap = filteredFlatMap;
    this.notifyObservers(); // Notify on set
  }

  getSelectedHiddenNodesSet() {
    return this.seletedHiddenNodesSet;
  }

  calculateSelectedHiddenNodesSet() {
    const nodesMap = this.getFlatMap();
    const filteredMap = this.getFilteredFlatMap();

    this.seletedHiddenNodesSet = new Set();
    nodesMap.forEach((item, key) => {
      if (item.selected && !filteredMap.has(key)) {
        this.seletedHiddenNodesSet.add(key);
      }
    });
  }

  setRootNode(rootNode: OurNode) {
    this.rootNode = rootNode;
  }

  addObserver(observer: () => void) {
    this.observers.add(observer);
  }

  removeObserver(observer: () => void) {
    this.observers.delete(observer);
  }

  notifyObservers() {
    this.observers.forEach((observer) => observer());
  }

  filterNodes(filterString: string): Map<string, OurNode> {
    const filteredFlatMap = new Map<string, OurNode>(
      Array.from(this.flatMap).filter(
        ([key, node]) =>
          node.name.toLowerCase().includes(filterString.toLowerCase()) ||
          node.id.toLowerCase().includes(filterString.toLowerCase()),
      ),
    );

    this.setFilteredFlatMap(filteredFlatMap);

    return filteredFlatMap;
  }

  deleteNodesContainedInSelectedNodesSet(): void {
    const valuesArray = Array.from(this.flatMap.values());
    const filteredElements = valuesArray.filter((item) => item.selected);
    const selectedIds = filteredElements.map((item) => item.id);

    this.selectedNodeIds = new Set(selectedIds);

    this.selectedNodeIds.forEach((id) => {
      if (this.flatMap.has(id)) {
        OurNode.deleteSelectedNodesFromNode(this.flatMap.get(id));

        if (this.filteredFlatMap.has(id)) {
          this.filteredFlatMap.delete(id);
        }
        this.selectedNodeIds.delete(id);
      }
    });

    const flatMap = this.rootNode.createMapFromOurNodeTree();
    this.setFlatMap(flatMap);
  }

  deleteNodesInSidePanel(node): void {
    OurNode.removeDraggedNodesFromPreviousLocationInMachineStructure(node);

    this.selectedNodeIds.delete(node.id);

    const flatMap = this.rootNode.createMapFromOurNodeTree();
    this.setFlatMap(flatMap);
  }

  initialToMachineDragAndDrop(
    initialStructureNodeset,
    machineStructureNodeset,
    targetNode,
  ): void {
    const updatedMachineStructure =
      OurNode.insertInitialStructureNodesInMachineStructureBetweenNodesAndReturnMachineStructure(
        initialStructureNodeset.rootNode,
        targetNode,
      );

    initialStructureNodeset.rootNode.deselectYourselfAndAllChildren();
    updatedMachineStructure.deselectYourselfAndAllChildren();

    initialStructureNodeset.setTransferredAttributeOnSelectedNodes(
      true,
      initialStructureNodeset.selectedNodeIds,
    );

    // Set transferred attribute to true for the root node when only a subset is dropped on an empty machine structure
    initialStructureNodeset.getRootNode().transfered = true;

    initialStructureNodeset.selectedNodeIds = new Set();

    const initialFlatMap =
      initialStructureNodeset.rootNode.createMapFromOurNodeTree();
    const machineFlatMap = updatedMachineStructure.createMapFromOurNodeTree();

    initialStructureNodeset.setFlatMap(initialFlatMap);
    machineStructureNodeset.selectedNodeIds = new Set();
    machineStructureNodeset.setFlatMap(machineFlatMap);
  }

  toggleTransferredAttributeOnSelectedNodes(
    selectedNodeIds: Set<string>,
  ): void {
    this.getFlatMap().forEach((node) => {
      if (selectedNodeIds.has(node.id)) {
        node.transfered = !node.transfered;
      }
    });
  }

  setTransferredAttributeOnSelectedNodes(
    transferred: boolean,
    selectedNodeIds: Set<string>,
  ): void {
    this.getFlatMap().forEach((node) => {
      if (selectedNodeIds.has(node.id)) {
        node.transfered = transferred;
      }
    });
  }

  initialToMachineFolderDragAndDrop(
    initialStructureNodeset: OurNodeset,
    machineStructureNodeset: OurNodeset,
    targetNode: OurNode,
  ): void {
    const newMachineStructure =
      OurNode.insertInitialStructureNodesInMachineStructureFolderNodeAndReturnMachineStructure(
        initialStructureNodeset.rootNode,
        targetNode,
      );

    initialStructureNodeset.rootNode.deselectYourselfAndAllChildren();
    newMachineStructure.deselectYourselfAndAllChildren();

    initialStructureNodeset.setTransferredAttributeOnSelectedNodes(
      true,
      initialStructureNodeset.selectedNodeIds,
    );

    // Set transferred attribute to true for the root node when only a subset is dropped on an empty machine structure
    initialStructureNodeset.getRootNode().transfered = true;

    initialStructureNodeset.selectedNodeIds = new Set();

    const initialFlatMap =
      initialStructureNodeset.rootNode.createMapFromOurNodeTree();
    const machineFlatMap = newMachineStructure.createMapFromOurNodeTree();

    initialStructureNodeset.setFlatMap(initialFlatMap);
    machineStructureNodeset.selectedNodeIds = new Set();
    machineStructureNodeset.setFlatMap(machineFlatMap);
  }

  machineToMachineDragAndDrop(
    machineStructureNodesetInstance,
    targetNode,
  ): void {
    const updatedMachineStructure =
      OurNode.insertMachineStructureNodesInMachineStructureBetweenNodesAndReturnMachineStructure(
        machineStructureNodesetInstance.rootNode,
        targetNode,
      );

    if (updatedMachineStructure) {
      updatedMachineStructure.deselectYourselfAndAllChildren();

      machineStructureNodesetInstance.selectedNodeIds = new Set();

      const machineFlatMap = updatedMachineStructure.createMapFromOurNodeTree();

      machineStructureNodesetInstance.setFlatMap(machineFlatMap);
    }
  }

  machineToMachineFolderDragAndDrop(
    machineStructureNodesetInstance,
    targetNode,
  ): void {
    const updatedMachineStructure =
      OurNode.insertMachineStructureNodesInMachineStructureFolderNodeAndReturnMachineStructure(
        machineStructureNodesetInstance.rootNode,
        targetNode,
      );

    if (updatedMachineStructure) {
      updatedMachineStructure.deselectYourselfAndAllChildren();

      machineStructureNodesetInstance.selectedNodeIds = new Set();

      const machineFlatMap = updatedMachineStructure.createMapFromOurNodeTree();

      machineStructureNodesetInstance.setFlatMap(machineFlatMap);
    }
  }

  deleteAllChildrenOnRootNode() {
    this.rootNode.children = [];
  }

  transferSelectedNodesToRootNode(
    initialStructureNodeset: OurNodeset,
    machineStructureNodeset: OurNodeset,
  ): void {
    machineStructureNodeset.deleteAllChildrenOnRootNode();

    const newMachineStructure =
      OurNode.insertInitialStructureNodesInMachineStructureFolderNodeAndReturnMachineStructure(
        initialStructureNodeset.rootNode,
        machineStructureNodeset.rootNode,
      );

    initialStructureNodeset.rootNode.deselectYourselfAndAllChildren();
    newMachineStructure.deselectYourselfAndAllChildren();

    initialStructureNodeset.selectedNodeIds = new Set();

    const initialFlatMap =
      initialStructureNodeset.rootNode.createMapFromOurNodeTree();
    const machineFlatMap = newMachineStructure.createMapFromOurNodeTree();

    new TransferredComparer(
      initialFlatMap,
      machineFlatMap,
    ).setFoundNodeToTransferred();

    initialStructureNodeset.setFlatMap(initialFlatMap);
    machineStructureNodeset.setFlatMap(machineFlatMap);
  }

  compareInitialAndMachineStructureNodesetsAndSetTransferred(
    initialSturctureNodesetInstance,
    machineStructureNodesetInstance,
  ): void {
    const initialFlatMap = initialSturctureNodesetInstance.getFlatMap();
    const machineFlatMap = machineStructureNodesetInstance.getFlatMap();

    new TransferredComparer(
      initialFlatMap,
      machineFlatMap,
    ).setFoundNodeToTransferred();
  }

  writeDataAcquisitionValuesInSelectedNodes(
    dataAcquisitionType: string,
    pollingIntervalTime: number,
  ): void {
    this.selectedNodeIds.forEach((id) => {
      if (this.flatMap.has(id)) {
        const node = this.flatMap.get(id);
        if (node.node_type === "VARIABLE") {
          const updatedAttributes = {
            ...node.attributes,
            data_acquisition_type: dataAcquisitionType,
            sample_interval_in_seconds: pollingIntervalTime,
          };
          node.attributes = updatedAttributes;
        }
      }
    });

    this.rootNode.deselectYourselfAndAllChildren();
    this.selectedNodeIds = new Set();
    const flatMap = this.rootNode.createMapFromOurNodeTree();
    this.setFlatMap(flatMap);
  }

  selectAllNodes(): void {
    const newSelectedIds = new Set<string>();

    this.rootNode.selectYourselfAndAllChildren((node) => {
      newSelectedIds.add(node.id);
    });

    this.setSelectedNodeIds(newSelectedIds);
  }

  deselectAllNodes(): void {
    this.rootNode.deselectYourselfAndAllChildren();
    this.setSelectedNodeIds(new Set());
  }

  flushSelectedNodeIdsWhenOverwritingNodeset(): void {
    this.setSelectedNodeIds(new Set());
  }

  selectFilteredNodes(): void {
    const filteredMap = this.getFilteredFlatMap();
    const newSelectedIds = new Set(this.selectedNodeIds);

    const select = (node) => {
      if (filteredMap.has(node.id)) {
        node.selected = true;
        newSelectedIds.add(node.id);
      }

      if (node.children) {
        node.children.forEach(select);
      }
    };

    select(this.getRootNode());

    this.setSelectedNodeIds(newSelectedIds);
  }

  deselectFilteredNodes(): void {
    const filteredMap = this.getFilteredFlatMap();
    const newSelectedIds = new Set(this.selectedNodeIds);

    const deselect = (node) => {
      if (filteredMap.has(node.id)) {
        node.selected = false;
        newSelectedIds.delete(node.id);
      }

      if (node.children) {
        node.children.forEach(deselect);
      }
    };

    deselect(this.getRootNode());

    this.setSelectedNodeIds(newSelectedIds);
  }
}
